// src/pages/stylist/SchedulePage.tsx
import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, isWithinInterval, startOfDay, endOfDay, parseISO } from 'date-fns';
import { useAppSelector } from '../../hooks/useRedux';
import { scheduleService, WorkingHours } from '../../services/scheduleService';
import TeslaButton from '../../components/common/TeslaButton';
import ScrollReveal from '../../components/common/ScrollReveal';

const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

const SchedulePage: React.FC = () => {
  const { user } = useAppSelector(state => state.auth);
  
  // State for working hours
  const [workingHours, setWorkingHours] = useState<WorkingHours[]>([]);
  
  // State for time slots editing
  const [selectedDay, setSelectedDay] = useState<string>('Monday');
  const [isEditing, setIsEditing] = useState(false);
  
  // State for schedule view
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [tabValue, setTabValue] = useState(0);
  
  // State for blocked dates
  const [blockedDates, setBlockedDates] = useState<Date[]>([]);
  const [isBlockingDate, setIsBlockingDate] = useState(false);
  
  // State for appointment slots
  // Using a custom extended TimeSlot type that includes UI-specific properties
  const [dailySlots, setDailySlots] = useState<Array<{
    id?: string; 
    startTime: string; 
    endTime: string;
    isAvailable?: boolean;
  }>>([]);
  
  // Add these for service selection
  const [selectedServiceId, setSelectedServiceId] = useState<string | undefined>(undefined);
  const [serviceDuration, setServiceDuration] = useState<number | undefined>(undefined);
  const [services, setServices] = useState<Array<{ id: string; name: string; duration: number }>>([]);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Initialize data on component mount
  useEffect(() => {
    if (user?.id) {
      fetchScheduleData();
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // fetchScheduleData and fetchServices are stable
  
  // Fetch services
  const fetchServices = async () => {
    try {
      // Replace with your actual API call
      // const response = await apiClient.get('/services');
      // setServices(response.data);
      
      // Mock data until the API is connected
      setServices([
        { id: 'service1', name: 'Haircut', duration: 30 },
        { id: 'service2', name: 'Hair Coloring', duration: 60 },
        { id: 'service3', name: 'Full Treatment', duration: 120 }
      ]);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };
  
  // Fetch all schedule data
  const fetchScheduleData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch working hours
      try {
        const scheduleResponse = await scheduleService.getStylistSchedule(user.id);
        setWorkingHours(scheduleResponse.defaultHours);
      } catch (scheduleError) {
        console.error('Error fetching working hours:', scheduleError);
        throw new Error('Failed to load working hours');
      }
      
      // Fetch blocked dates
      try {
        const blockedDatesResponse = await scheduleService.getBlockedDates(user.id);
        const parsedBlockedDates = blockedDatesResponse.map(blockedDate => 
          parseISO(blockedDate.date)
        );
        setBlockedDates(parsedBlockedDates);
      } catch (blockedDatesError) {
        console.error('Error fetching blocked dates:', blockedDatesError);
        throw new Error('Failed to load blocked dates');
      }
      
      // Load slots for today
      try {
        await loadDailySlots(selectedDate);
      } catch (slotsError) {
        console.error('Error loading daily slots:', slotsError);
        throw new Error('Failed to load daily slots');
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setError('Failed to load schedule data. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Load slots for a specific date
  const loadDailySlots = async (date: Date) => {
    if (!user?.id) return;
    
    setIsLoading(true);
    
    // Check if date is blocked
    const isBlocked = blockedDates.some(blockedDate => 
      isWithinInterval(date, {
        start: startOfDay(blockedDate),
        end: endOfDay(blockedDate)
      })
    );
    
    if (isBlocked) {
      setDailySlots([]);
      setIsLoading(false);
      return;
    }
    
    try {
      const slotsResponse = await scheduleService.getAvailableSlots(
        user.id, 
        date, 
        selectedServiceId, 
        serviceDuration
      );
      
      // Extract and convert the data array to a compatible format for UI
      const convertedSlots = slotsResponse.data.map(slot => ({
        startTime: slot.startTime,
        endTime: slot.endTime,
        isAvailable: true // Default to available
      }));
      
      setDailySlots(convertedSlots);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading daily slots:', error);
      setError('Failed to load time slots. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Handle working hours toggle
  const handleWorkingDayToggle = (day: string) => {
    const dayHours = workingHours.find(hours => hours.day === day);
    if (!dayHours) return;
    
    const updatedHours = workingHours.map(hours => 
      hours.day === day 
        ? { 
            ...hours, 
            isWorkingDay: !hours.isWorkingDay,
            shifts: !hours.isWorkingDay 
              ? [{ id: `${day}-0`, startTime: '09:00', endTime: '17:00', isAvailable: true }] 
              : []
          } 
        : hours
    );
    
    setWorkingHours(updatedHours);
  };
  
  // Handle adding a new time slot
  const handleAddSlot = (day: string) => {
    const dayHours = workingHours.find(hours => hours.day === day);
    if (!dayHours) return;
    
    // Create a shift that matches WorkingHours.shifts
    const newSlot = {
      startTime: '09:00',
      endTime: '17:00'
    };
    
    setWorkingHours(prevHours => prevHours.map(hours => {
      if (hours.day === day) {
        return {
          ...hours,
          shifts: [...hours.shifts, newSlot]
        };
      }
      return hours;
    }));
  };
  
  // Handle deleting a time slot
  const handleDeleteSlot = (day: string, shiftIndex: number) => {
    setWorkingHours(prevHours => prevHours.map(hours => {
      if (hours.day === day) {
        return {
          ...hours,
          shifts: hours.shifts.filter((_, index) => index !== shiftIndex)
        };
      }
      return hours;
    }));
  };
  
  // Handle updating a time slot
  const handleUpdateSlot = (day: string, shiftIndex: number, field: string, value: string | boolean) => {
    setWorkingHours(prevHours => prevHours.map(hours => {
      if (hours.day === day) {
        return {
          ...hours,
          shifts: hours.shifts.map((slot, index) => {
            if (index === shiftIndex) {
              return { ...slot, [field]: value };
            }
            return slot;
          })
        };
      }
      return hours;
    }));
  };
  
  // Handle saving working hours
  const handleSaveWorkingHours = async () => {
    if (!user?.id || isLoading) return;
    
    try {
      setIsLoading(true);
      await scheduleService.updateWorkingHours(workingHours);
      setSuccess('Working hours updated successfully');
      setIsEditing(false);
      setIsLoading(false);
    } catch (error) {
      console.error('Error saving working hours:', error);
      setError('Failed to save working hours. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Handle blocking a date
  const handleBlockDate = async (date: Date) => {
    if (!user?.id) return;
    
    try {
      setIsLoading(true);
      const dateStr = format(date, 'yyyy-MM-dd');
      await scheduleService.blockDate(user.id, dateStr, 'Not available');
      setBlockedDates(prevDates => [...prevDates, date]);
      setIsBlockingDate(false);
      setSuccess('Date blocked successfully');
      setIsLoading(false);
      
      // Reload daily slots if the blocked date is the selected date
      if (isWithinInterval(selectedDate, { start: startOfDay(date), end: endOfDay(date) })) {
        loadDailySlots(selectedDate);
      }
    } catch (error) {
      console.error('Error blocking date:', error);
      setError('Failed to block date. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Handle unblocking a date
  const handleUnblockDate = async (date: Date) => {
    if (!user?.id) return;
    
    try {
      setIsLoading(true);
      // We need to find the blockedDate ID to unblock it
      // For now, just use a placeholder approach
      const blockedDateId = "placeholder"; // This should be retrieved from API
      await scheduleService.unblockDate(blockedDateId);
      
      setBlockedDates(prevDates => prevDates.filter(blockedDate => 
        !isWithinInterval(blockedDate, {
          start: startOfDay(date),
          end: endOfDay(date)
        })
      ));
      
      setSuccess('Date unblocked successfully');
      setIsLoading(false);
      
      // Reload daily slots if the unblocked date is the selected date
      if (isWithinInterval(selectedDate, { start: startOfDay(date), end: endOfDay(date) })) {
        loadDailySlots(selectedDate);
      }
    } catch (error) {
      console.error('Error unblocking date:', error);
      setError('Failed to unblock date. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Handle service selection change
  const handleServiceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const serviceId = event.target.value || undefined;
    setSelectedServiceId(serviceId);
    
    // Update service duration if available
    if (serviceId) {
      const selectedService = services.find(service => service.id === serviceId);
      setServiceDuration(selectedService?.duration);
    } else {
      setServiceDuration(undefined);
    }
    
    // Reload slots with the new service selection
    if (selectedDate) {
      loadDailySlots(selectedDate);
    }
  };
  
  // Handle tab change
  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      loadDailySlots(date);
    }
  };
  
  // Clear notifications
  const clearNotification = () => {
    setError(null);
    setSuccess(null);
  };
  
  // Render time slot editor
  const renderTimeSlotEditor = () => {
    const dayHours = workingHours.find(hours => hours.day === selectedDay);
    
    if (!dayHours) return null;
    
    return (
      <div className="mt-6">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="text-heading-3">
            {selectedDay} Hours
          </h3>
          
          <div className="form-check form-switch">
            <label className="form-check-label">
              <input
                type="checkbox"
                className="form-check-input"
                checked={dayHours.isWorkingDay}
                onChange={() => handleWorkingDayToggle(selectedDay)}
                disabled={!isEditing}
              />
              {dayHours.isWorkingDay ? 'Working Day' : 'Day Off'}
            </label>
          </div>
        </div>
        
        {dayHours.isWorkingDay && (
          <>
            {dayHours.shifts.map((slot, index) => (
              <div key={index} className="d-flex align-items-center mb-4">
                <div className="form-group me-4" style={{ width: '30%' }}>
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={slot.startTime}
                    onChange={(e) => handleUpdateSlot(selectedDay, index, 'startTime', e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
                
                <div className="form-group me-4" style={{ width: '30%' }}>
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={slot.endTime}
                    onChange={(e) => handleUpdateSlot(selectedDay, index, 'endTime', e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
                
                <div className="form-check form-switch">
                  <label className="form-check-label">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={false} // Default state
                      onChange={(e) => console.log('Availability toggle clicked')} // Just log for now
                      disabled={!isEditing}
                    />
                    Available
                  </label>
                </div>
                
                {isEditing && (
                  <button 
                    className="btn btn--icon text-error ms-2" 
                    onClick={() => handleDeleteSlot(selectedDay, index)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
            
            {isEditing && (
              <TeslaButton
                onClick={() => handleAddSlot(selectedDay)}
                variant="secondary"
                className="mt-3"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Add Time Slot
              </TeslaButton>
            )}
          </>
        )}
      </div>
    );
  };
  
  // Render blocked dates manager
  const renderBlockedDatesManager = () => {
    const isCurrentDateBlocked = blockedDates.some(blockedDate => 
      isWithinInterval(selectedDate, {
        start: startOfDay(blockedDate),
        end: endOfDay(blockedDate)
      })
    );
    
    return (
      <div className="mt-6">
        <div className="tesla-card p-6 mb-6">
          <h3 className="text-heading-3 mb-4">
            Block or Unblock Dates
          </h3>
          
          <p className="text-body-small text-primary-300 mb-4">
            Select a date on the calendar to block or unblock it.
          </p>
          
          <div className="d-flex justify-content-center my-4">
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              modifiers={{
                blocked: blockedDates,
                today: new Date()
              }}
              modifiersStyles={{
                blocked: { backgroundColor: '#ffcccc', color: '#990000' },
                today: { fontWeight: 'bold', fontSize: '120%' }
              }}
            />
          </div>
          
          <div className="d-flex justify-content-center mt-4">
            {isCurrentDateBlocked ? (
              <TeslaButton
                variant="secondary"
                onClick={() => handleUnblockDate(selectedDate)}
                loading={isLoading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                  <path d="M17 12h-5v5h-2v-5H5v-2h5V5h2v5h5v2z"/>
                </svg>
                Unblock {format(selectedDate, 'MMMM d, yyyy')}
              </TeslaButton>
            ) : (
              <TeslaButton
                variant="primary"
                onClick={() => setIsBlockingDate(true)}
                loading={isLoading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                  <path d="M19 13H5v-2h14v2z"/>
                </svg>
                Block {format(selectedDate, 'MMMM d, yyyy')}
              </TeslaButton>
            )}
          </div>
        </div>
        
        <div className="tesla-card p-6">
          <h3 className="text-heading-3 mb-4">
            Blocked Dates
          </h3>
          
          {blockedDates.length === 0 ? (
            <p className="text-body-small text-primary-300">
              No dates are currently blocked.
            </p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {blockedDates.map(date => (
                <div 
                  key={date.toISOString()}
                  className="px-3 py-2 rounded bg-error-100 text-error d-flex align-items-center gap-2"
                >
                  <span>{format(date, 'MMMM d, yyyy')}</span>
                  <button 
                    className="btn btn--icon-sm"
                    onClick={() => handleUnblockDate(date)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Render daily slots
  const renderDailySlots = () => {
    const dayOfWeek = daysOfWeek[selectedDate.getDay()];
    const dayHours = workingHours.find(hours => hours.day === dayOfWeek);
    
    // Check if this is a working day
    if (!dayHours || !dayHours.isWorkingDay) {
      return (
        <div className="alert alert--info mt-6">
          <strong>Not a Working Day</strong>
          <p>{dayOfWeek} is set as a day off.</p>
        </div>
      );
    }
    
    // Check if this date is blocked
    const isBlocked = blockedDates.some(blockedDate => 
      isWithinInterval(selectedDate, {
        start: startOfDay(blockedDate),
        end: endOfDay(blockedDate)
      })
    );
    
    if (isBlocked) {
      return (
        <div className="alert alert--warning mt-6">
          <strong>Date Blocked</strong>
          <p>{format(selectedDate, 'MMMM d, yyyy')} is blocked for appointments.</p>
        </div>
      );
    }
    
    if (isLoading) {
      return (
        <div className="d-flex justify-content-center mt-8">
          <div className="loader"></div>
        </div>
      );
    }
    
    return (
      <div className="mt-6">
        <h3 className="text-heading-3 mb-4">
          Time Slots for {format(selectedDate, 'MMMM d, yyyy')}
        </h3>
        
        {/* Service selection dropdown */}
        <div className="form-group mb-4">
          <label className="form-label">Filter by Service</label>
          <select
            className="form-select"
            value={selectedServiceId || ''}
            onChange={handleServiceChange}
          >
            <option value="">All Services</option>
            {services.map(service => (
              <option key={service.id} value={service.id}>
                {service.name} ({service.duration} min)
              </option>
            ))}
          </select>
        </div>
        
        {dailySlots.length === 0 ? (
          <div className="alert alert--info">
            <p>No time slots available for this date{selectedServiceId ? ' and service' : ''}.</p>
          </div>
        ) : (
          <div className="grid grid--3-col">
            {dailySlots.map((slot, index) => (
              <div key={index} className="tesla-card p-4">
                <h4 className="text-heading-4 mb-2">
                  {slot.startTime} - {slot.endTime}
                </h4>
                <div className={`tag-button active ${slot.isAvailable ? 'bg-success-100 text-success' : 'bg-error-100 text-error'}`}>
                  {slot.isAvailable ? 'Available' : 'Unavailable'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <ScrollReveal animation="fade-in-up">
      <div className="container py-8">
        <h1 className="text-heading-2 mb-6">Schedule Management</h1>
        
        {/* Notifications */}
        {error && (
          <div className="notification notification--error mb-4">
            <span>{error}</span>
            <button className="notification__close" onClick={clearNotification}>×</button>
          </div>
        )}
        
        {success && (
          <div className="notification notification--success mb-4">
            <span>{success}</span>
            <button className="notification__close" onClick={clearNotification}>×</button>
          </div>
        )}
        
        <div className="tabs mb-6">
          <button 
            className={`tab-button ${tabValue === 0 ? 'active' : ''}`}
            onClick={() => handleTabChange(0)}
          >
            Working Hours
          </button>
          <button 
            className={`tab-button ${tabValue === 1 ? 'active' : ''}`}
            onClick={() => handleTabChange(1)}
          >
            Blocked Dates
          </button>
          <button 
            className={`tab-button ${tabValue === 2 ? 'active' : ''}`}
            onClick={() => handleTabChange(2)}
          >
            Daily View
          </button>
        </div>
        
        {isLoading && tabValue !== 2 && (
          <div className="availability-loading mb-6">
            <div className="loader"></div>
            <p>Loading...</p>
          </div>
        )}
        
        {tabValue === 0 && !isLoading && (
          <div className="tesla-card p-6">
            <div className="d-flex justify-content-between align-items-center mb-6">
              <h2 className="text-heading-3">
                Weekly Working Hours
              </h2>
              
              {isEditing ? (
                <TeslaButton
                  onClick={handleSaveWorkingHours}
                  variant="primary"
                  loading={isLoading}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                    <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
                  </svg>
                  Save
                </TeslaButton>
              ) : (
                <TeslaButton
                  onClick={() => setIsEditing(true)}
                  variant="secondary"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  Edit Hours
                </TeslaButton>
              )}
            </div>
            
            {/* Day selector */}
            <div className="form-group mb-6">
              <label className="form-label mb-2">Select Day</label>
              <select
                className="form-select"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
              >
                {daysOfWeek.map(day => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Time slot editor */}
            {renderTimeSlotEditor()}
          </div>
        )}
        
        {tabValue === 1 && !isLoading && renderBlockedDatesManager()}
        
        {tabValue === 2 && (
          <div className="tesla-card p-6">
            <div className="split">
              <div className="split__left">
                <div className="d-flex justify-content-center mb-4">
                  <DayPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    modifiers={{
                      blocked: blockedDates,
                      today: new Date()
                    }}
                    modifiersStyles={{
                      blocked: { backgroundColor: '#ffcccc', color: '#990000' },
                      today: { fontWeight: 'bold', fontSize: '120%' }
                    }}
                  />
                </div>
              </div>
              
              <div className="split__right">
                {renderDailySlots()}
              </div>
            </div>
          </div>
        )}
        
        {/* Confirmation Dialog for Blocking Date */}
        {isBlockingDate && (
          <div className="modal active">
            <div className="modal__backdrop" onClick={() => setIsBlockingDate(false)}></div>
            <div className="modal__container">
              <div className="modal__header">
                <h3 className="modal__title">Confirm Date Block</h3>
                <button 
                  className="modal__close" 
                  onClick={() => setIsBlockingDate(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
              <div className="modal__body">
                <p>
                  Are you sure you want to block {format(selectedDate, 'MMMM d, yyyy')}? This will make this date unavailable for appointments.
                </p>
              </div>
              <div className="modal__footer">
                <TeslaButton 
                  variant="secondary" 
                  onClick={() => setIsBlockingDate(false)}
                >
                  Cancel
                </TeslaButton>
                <TeslaButton 
                  variant="primary" 
                  onClick={() => handleBlockDate(selectedDate)}
                  loading={isLoading}
                >
                  Block Date
                </TeslaButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollReveal>
  );
};

export default SchedulePage;